import { Dialog } from '@angular/cdk/dialog';
import { Component, DestroyRef, inject, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApplicationStatusExecutionEnum, AssessmentApplicationMode } from '@app/core/models';
import { AssessmentApplicationService } from '@app/core/services';
import { ModalAssessmentsReportComponent } from '@app/modules/assessments-manager/shared/components/modal-evaluation-report/modal-assessments-report.component';
import { ModalAssessmentExecutionService } from '@app/modules/assessments-manager/shared/modal-assessment-execution/modal-assessment-execution.service';
import { Checker, notNull, safeEmptyList, switchDelay } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { format } from 'date-fns';
import { combineLatest, debounceTime, Observable, startWith, Subject, switchMap, tap } from 'rxjs';

@Component({
  selector: 'app-card-evaluation',
  templateUrl: './card-evaluation.component.html',
  standalone: false,
})
export class CardEvaluationComponent implements OnDestroy {
  private readonly _dialog = inject(Dialog);
  private readonly _assessmentApplication = inject(AssessmentApplicationService);
  private readonly _store = inject(Store);
  private readonly _translateService = inject(TranslateService);
  private readonly _destroy = inject(DestroyRef);
  private readonly _modalAssessmentExecutionService = inject(ModalAssessmentExecutionService);
  public readonly applicationStatusExecutionEnum = ApplicationStatusExecutionEnum;
  public readonly assessmentApplicationMode = AssessmentApplicationMode;
  public evaluations$: Observable<any>;
  public entityId$: Observable<number>;
  public load$: Observable<boolean>;
  private _load$ = new Subject<boolean>();
  public modos = [
    {
      id: AssessmentApplicationMode.Online,
      label: this._translateService.instant('gestor-avaliacoes.modos.online'),
    },
    {
      id: AssessmentApplicationMode.Impressa,
      label: this._translateService.instant('gestor-avaliacoes.modos.impressa'),
    },
    {
      id: AssessmentApplicationMode.Hibrido,
      label: this._translateService.instant('gestor-avaliacoes.modos.hibrido'),
    },
  ];

  public isStudent: boolean = false;
  public readonly badgeStyle = {
    background: 'bg-green-400 text-white',
  };

  constructor() {
    this.load$ = this._load$.asObservable().pipe(switchDelay(), startWith(true));

    const activeUser$ = this._store.select(AppSelectors.ActiveUser).pipe(notNull());

    this.evaluations$ = combineLatest([
      activeUser$,
      this._store.select(AppSelectors.DefaultFunction).pipe(notNull()),
    ]).pipe(
      takeUntilDestroyed(this._destroy),
      debounceTime(200),
      tap(([activeUser, defaultFunction]) => {
        this.isStudent = Checker.isStudentOrResponsible(defaultFunction);
        this._load$.next(true);
      }),
      switchMap(([activeUser]) => {
        const data: {
          entity_id: number;
          except_status: ApplicationStatusExecutionEnum.Closed;
          year?: string;
          month?: string;
        } = {
          entity_id: activeUser.entidade_id,
          except_status: ApplicationStatusExecutionEnum.Closed,
        };

        if (!this.isStudent) {
          data.year = format(new Date(), 'yyyy');
          data.month = format(new Date(), 'MM');
        }

        return this._assessmentApplication.assessmentEclass(data).pipe(safeEmptyList());
      }),
      tap(() => this._load$.next(false)),
    );
  }

  public ngOnDestroy() {
    this._load$.complete();
  }

  public onExecuteAssessment(applicationUuid: string) {
    this._modalAssessmentExecutionService.open({
      data: { aplicacao_uuid: applicationUuid },
    });
  }

  public onModalEvaluation(application: any) {
    this._dialog.open(ModalAssessmentsReportComponent, {
      data: application,
    });
  }

  public onAssessmentClick(application: any) {
    if (this.isStudent) {
      this.onExecuteAssessment(application.uuid);
    } else {
      this.onModalEvaluation(application);
    }
  }
}
